import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  DIFFICULTY_PRESETS,
  GAME_MODES,
  ACTIVE_SKILLS,
  ULT_SKILLS,
  type DifficultyPresetKey,
  type GameModeKey,
  type ActiveSkillKey,
  type UltSkillKey,
} from '../config/GameConfig';
import { buildAllTextures } from '../systems/AssetFactory';
import { loadHighScore, resetHighScore, loadCampaignClears } from '../systems/highScore';
import { toggleFullscreen, isFullscreen, isFullscreenSupported } from '../systems/fullscreen';

interface MenuButton {
  container: Phaser.GameObjects.Container;
  label: Phaser.GameObjects.Text;
}

/**
 * MenuScene —— 主菜单
 *
 * 内容：标题 / 装饰 / 三按钮（开始游戏 / 难度切换 / 重置最高分）/ 底部最高分
 * 选中的难度通过 scene.registry.set('difficultyPreset', key) 写入，
 * MainScene 的 Difficulty 启动时读取。
 */
export default class MenuScene extends Phaser.Scene {
  private _diffBtn!: MenuButton;
  private _modeBtn!: MenuButton;
  private _skillBtn: MenuButton | null = null;
  private _highScoreText!: Phaser.GameObjects.Text;
  private _moreToggleBtn!: MenuButton;
  private _moreOptionsContainer: Phaser.GameObjects.Container | null = null;
  private _moreOptionsVisible = false;

  constructor() {
    super('MenuScene');
  }

  preload(): void {
    buildAllTextures(this);
  }

  create(): void {
    if (!this.registry.get('difficultyPreset')) {
      this.registry.set('difficultyPreset', 'normal');
    }
    if (!this.registry.get('gameMode')) {
      this.registry.set('gameMode', 'campaign');
    }
    // 技能 loadout 默认值（A1=dash, A2=pulse, ULT=nuke）
    if (!this.registry.get('skillA1'))  this.registry.set('skillA1',  'dash');
    if (!this.registry.get('skillA2'))  this.registry.set('skillA2',  'pulse');
    if (!this.registry.get('skillUlt')) this.registry.set('skillUlt', 'nuke');

    // 背景：焦土战场 + 烟尘光晕，和局内背景保持同一战争氛围
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x141812).setOrigin(0, 0);
    this.add.circle(GAME_WIDTH * 0.18, 92, 150, 0x2d3540, 0.2);
    this.add.circle(GAME_WIDTH * 0.82, 170, 190, 0x3a1b14, 0.22);
    this.add.circle(GAME_WIDTH * 0.5, GAME_HEIGHT - 116, 220, 0x4a2a12, 0.14);
    this.add
      .tileSprite(0, GAME_HEIGHT - 190, GAME_WIDTH, 190, 'grass-tile')
      .setOrigin(0, 0)
      .setAlpha(0.88);
    this.add.rectangle(0, GAME_HEIGHT - 190, GAME_WIDTH, 1, 0xff8a3c, 0.22).setOrigin(0, 0);
    this.add.rectangle(34, GAME_HEIGHT - 190, 3, 190, 0x0a0c09, 0.28).setOrigin(0, 0);
    this.add.rectangle(GAME_WIDTH - 37, GAME_HEIGHT - 190, 3, 190, 0x0a0c09, 0.28).setOrigin(0, 0);
    this.add.image(64, GAME_HEIGHT - 142, 'obstacle-sandbag').setAlpha(0.72).setScale(0.85).setAngle(-6);
    this.add.image(GAME_WIDTH - 72, GAME_HEIGHT - 108, 'obstacle-barricade').setAlpha(0.7).setScale(0.78).setAngle(7);
    this.add.image(GAME_WIDTH / 2 + 110, GAME_HEIGHT - 174, 'obstacle-rubble').setAlpha(0.55).setScale(0.74);
    for (let i = 0; i < 18; i++) {
      this.add
        .image(Phaser.Math.Between(12, GAME_WIDTH - 12), Phaser.Math.Between(36, GAME_HEIGHT - 120), 'star')
        .setTint(i % 3 === 0 ? 0xff8a3c : 0x7a7768)
        .setAlpha(i % 3 === 0 ? 0.32 : 0.18)
        .setScale(i % 3 === 0 ? 2.4 : 2);
    }

    // 标题
    const titleY = 90;
    this.add
      .rectangle(GAME_WIDTH / 2, titleY + 4, 276, 78, 0x000000, 0.18)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0xffd166, 0.18);
    this.add
      .text(GAME_WIDTH / 2, titleY, '守桥小队', {
        fontFamily: 'sans-serif',
        fontSize: '52px',
        fontStyle: 'bold',
        color: '#ffd166',
        stroke: '#3a2a08',
        strokeThickness: 6,
      })
      .setOrigin(0.5);
    this.add
      .text(GAME_WIDTH / 2, 145, 'Bridge Defenders', {
        fontFamily: 'sans-serif',
        fontSize: '14px',
        color: '#aac4d8',
      })
      .setOrigin(0.5);

    // 装饰：左右拉开，不挤在标题正下方
    this.add.image(GAME_WIDTH / 2 - 122, 230, 'mob-straw').setScale(1.04);
    this.add.image(GAME_WIDTH / 2 + 126, 208, 'player-tank').setScale(1.02);

    // 按钮：主入口突出，次级入口默认收进“更多选项”
    const buttonY = 296;
    this._buildButton(GAME_WIDTH / 2, buttonY, '开始游戏', '#4cd2ff', () => {
      this.scene.start('MainScene');
    }, { width: 270, height: 64, fontSize: '22px' });

    this._modeBtn = this._buildButton(GAME_WIDTH / 2 - 105, buttonY + 78, '', '#9ee37d', () => {
      this._cycleMode();
    }, { width: 198, height: 58, fontSize: '14px' });
    this._refreshModeBtn();

    this._diffBtn = this._buildButton(GAME_WIDTH / 2 + 105, buttonY + 78, '', '#ffd166', () => {
      this._cycleDifficulty();
    }, { width: 198, height: 58, fontSize: '16px' });
    this._refreshDifficultyBtn();

    this._moreToggleBtn = this._buildButton(GAME_WIDTH / 2, buttonY + 148, '更多选项 ▾', '#aac4d8', () => {
      this._toggleMoreOptions();
    }, { width: 220, height: 48, fontSize: '15px' });

    this._buildMoreOptions(buttonY + 206);

    // 右上角全屏按钮（如浏览器支持）
    if (isFullscreenSupported()) {
      this._buildFullscreenButton();
    }

    // 底部最高分（按当前模式显示，且附带通关次数）
    this._highScoreText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 56, this._highScoreLine(), {
        fontFamily: 'sans-serif',
        fontSize: '13px',
        color: '#aac4d8',
        align: 'center',
      })
      .setOrigin(0.5);
    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 30, '战场内拖动移动 · 右下角技能 · 自动开火', {
        fontFamily: 'sans-serif',
        fontSize: '11px',
        color: '#5a6a80',
      })
      .setOrigin(0.5);

    // 场景退出时清掉教学弹窗 / 技能选择弹窗（防 DOM 残留）
    this.events.once('shutdown', () => {
      this._destroyHowToPlay();
      this._destroySkillPicker();
      this._moreOptionsContainer?.destroy();
      this._moreOptionsContainer = null;
    });
  }

  private _highScoreLine(): string {
    const mode = (this.registry.get('gameMode') as GameModeKey) || 'campaign';
    const cfg = GAME_MODES[mode];
    const high = loadHighScore(mode);
    if (mode === 'campaign') {
      const clears = loadCampaignClears();
      return `${cfg.label}最高 ${high}　通关 ${clears} 次`;
    }
    return `${cfg.label}最高 ${high}`;
  }

  private _refreshDifficultyBtn(): void {
    const key = (this.registry.get('difficultyPreset') as DifficultyPresetKey) || 'normal';
    const label = DIFFICULTY_PRESETS[key]?.label ?? '普通';
    this._diffBtn.label.setText(`难度：${label}`);
  }

  private _refreshSkillBtn(btn?: MenuButton): void {
    const a1 = (this.registry.get('skillA1') as ActiveSkillKey) || 'dash';
    const a2 = (this.registry.get('skillA2') as ActiveSkillKey) || 'pulse';
    const u  = (this.registry.get('skillUlt') as UltSkillKey)  || 'nuke';
    const a1cfg = ACTIVE_SKILLS[a1];
    const a2cfg = ACTIVE_SKILLS[a2];
    const ucfg  = ULT_SKILLS[u];
    const target = btn ?? this._skillBtn;
    target?.label.setText(`技能：${a1cfg.icon}${a2cfg.icon}${ucfg.icon}`);
  }

  private _refreshModeBtn(): void {
    const key = (this.registry.get('gameMode') as GameModeKey) || 'campaign';
    const cfg = GAME_MODES[key];
    // 按钮上一行模式名 + 一行小字描述
    this._modeBtn.label.setText(`模式：${cfg.label}\n${cfg.desc}`);
    // 同时刷新底部最高分行（不同模式榜不同）
    if (this._highScoreText) {
      this._highScoreText.setText(this._highScoreLine());
    }
  }

  private _toggleMoreOptions(): void {
    this._moreOptionsVisible = !this._moreOptionsVisible;
    this._moreOptionsContainer?.setVisible(this._moreOptionsVisible);
    this._moreToggleBtn.label.setText(this._moreOptionsVisible ? '收起选项 ▴' : '更多选项 ▾');
  }

  /** 次级入口：默认收起，避免首页按钮全部挤在一起 */
  private _buildMoreOptions(y: number): void {
    const c = this.add.container(0, 0).setVisible(false);
    this._moreOptionsContainer = c;

    const skillBtn = this._buildButton(GAME_WIDTH / 2, y, '', '#c084ff', () => {
      this._showSkillPicker();
    }, { width: 230, height: 48, fontSize: '15px' });
    this._skillBtn = skillBtn;
    this._refreshSkillBtn(skillBtn);
    c.add(skillBtn.container);

    const howToBtn = this._buildButton(GAME_WIDTH / 2 - 75, y + 56, '怎么玩', '#9ee37d', () => {
      this._showHowToPlay();
    }, { width: 140, height: 44, fontSize: '14px' });
    c.add(howToBtn.container);

    const resetBtn = this._buildButton(GAME_WIDTH / 2 + 75, y + 56, '重置最高分', '#ff8a8a', () => {
      this._resetHighScore();
    }, { width: 140, height: 44, fontSize: '14px' });
    c.add(resetBtn.container);
  }
  private _cycleDifficulty(): void {
    const order: DifficultyPresetKey[] = ['easy', 'normal', 'hard'];
    const cur = (this.registry.get('difficultyPreset') as DifficultyPresetKey) || 'normal';
    const next = order[(order.indexOf(cur) + 1) % order.length];
    this.registry.set('difficultyPreset', next);
    this._refreshDifficultyBtn();
  }

  private _cycleMode(): void {
    const order: GameModeKey[] = ['campaign', 'endless'];
    const cur = (this.registry.get('gameMode') as GameModeKey) || 'campaign';
    const next = order[(order.indexOf(cur) + 1) % order.length];
    this.registry.set('gameMode', next);
    this._refreshModeBtn();
  }

  // ─── 怎么玩 教学弹窗（DOM）─────────────────────────────────────

  private _howToEl: HTMLElement | null = null;
  private _howToPage = 0;

  private _showHowToPlay(): void {
    this._destroyHowToPlay();
    this._howToPage = 0;
    this._howToEl = this._buildHowToDom();
    document.body.appendChild(this._howToEl);
  }

  private _destroyHowToPlay(): void {
    if (this._howToEl?.parentNode) {
      this._howToEl.parentNode.removeChild(this._howToEl);
    }
    this._howToEl = null;
  }

  // ─── 技能选择弹窗（DOM）─────────────────────────────────────

  private _skillPickerEl: HTMLElement | null = null;

  private _showSkillPicker(): void {
    this._destroySkillPicker();
    this._skillPickerEl = this._buildSkillPickerDom();
    document.body.appendChild(this._skillPickerEl);
  }

  private _destroySkillPicker(): void {
    if (this._skillPickerEl?.parentNode) {
      this._skillPickerEl.parentNode.removeChild(this._skillPickerEl);
    }
    this._skillPickerEl = null;
  }

  /**
   * 技能选择 DOM：3 行 —— A1 / A2 / ULT
   * 每行展示该槽的所有候选（横向横滑卡片），高亮已选中。
   * A1/A2 不能选同一个（避免双 dash），切换时如果冲突自动把另一槽换成下一个候选。
   */
  private _buildSkillPickerDom(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; inset: 0;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,0,0,.82);
      backdrop-filter: blur(3px);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      padding: 16px;
      box-sizing: border-box;
    `;
    const panel = document.createElement('div');
    panel.style.cssText = `
      width: min(92vw, 420px);
      max-height: calc(100vh - 32px);
      overflow-y: auto;
      padding: 18px 18px 14px;
      border-radius: 18px;
      background: linear-gradient(180deg, #1d2a44 0%, #0e1424 100%);
      box-shadow: 0 18px 60px rgba(0,0,0,.65), 0 0 0 1px rgba(192,132,255,.32) inset, 0 0 34px rgba(192,132,255,.12);
      color: #e8f1ff;
    `;
    overlay.appendChild(panel);

    const renderAll = () => {
      panel.innerHTML = `
        <div style="font-size:18px; font-weight:600; color:#c084ff; margin-bottom:4px;">技能配置</div>
        <div style="font-size:11px; color:#5a6a80; margin-bottom:14px;">
          手机：右下角竖列三个按钮 · PC 键盘：C = 大招，X = 技能 2，空格/Z = 技能 1
        </div>
        <div data-row="ult"></div>
        <div data-row="a2"></div>
        <div data-row="a1"></div>
        <div data-row="footer" style="margin-top:14px;"></div>
      `;
      this._renderSkillRow(panel.querySelector('[data-row="ult"]')!, '大招（最上按钮）· 击杀蓄能后释放', 'skillUlt', Object.values(ULT_SKILLS), () => renderAll());
      this._renderSkillRow(panel.querySelector('[data-row="a2"]')!, '技能 2（中间按钮）· 冷却复用', 'skillA2', Object.values(ACTIVE_SKILLS), () => renderAll());
      this._renderSkillRow(panel.querySelector('[data-row="a1"]')!, '技能 1（最下按钮）· 冷却复用', 'skillA1', Object.values(ACTIVE_SKILLS), () => renderAll());

      const footer = panel.querySelector('[data-row="footer"]') as HTMLElement;
      const close = document.createElement('button');
      close.textContent = '完成';
      close.style.cssText = `
        width: 100%; padding: 11px;
        border-radius: 8px;
        border: 1px solid #c084ff;
        background: rgba(192,132,255,.15);
        color: #c084ff;
        font-size: 14px; font-weight: 600;
        cursor: pointer;
      `;
      close.addEventListener('click', () => this._destroySkillPicker());
      footer.appendChild(close);
    };
    renderAll();
    return overlay;
  }

  /**
   * 渲染一行技能选项 —— title 在上，下面是候选卡片横向（实际用 flex-wrap）。
   * 选中后写 registry，并冲突解除：A1/A2 不能同 key（自动把另一槽换成第一个不冲突的）。
   */
  private _renderSkillRow(
    container: HTMLElement,
    title: string,
    registryKey: 'skillA1' | 'skillA2' | 'skillUlt',
    options: Array<{ key: string; label: string; icon: string; shortDesc: string; tint: number }>,
    onChange: () => void,
  ): void {
    const wrap = document.createElement('div');
    wrap.style.cssText = `margin-bottom: 14px;`;
    const head = document.createElement('div');
    head.textContent = title;
    head.style.cssText = `font-size:12px; color:#aac4d8; margin-bottom:6px; letter-spacing:.05em;`;
    wrap.appendChild(head);
    const grid = document.createElement('div');
    grid.style.cssText = `display:flex; flex-wrap:wrap; gap:6px;`;
    const current = this.registry.get(registryKey) as string;
    for (const opt of options) {
      const tintHex = '#' + opt.tint.toString(16).padStart(6, '0');
      const isSelected = opt.key === current;
      const card = document.createElement('button');
      card.type = 'button';
      card.style.cssText = `
        flex: 1 1 calc(50% - 3px); min-width: 130px;
        padding: 10px;
        border-radius: 9px;
        border: 1.5px solid ${isSelected ? tintHex : 'rgba(255,255,255,.18)'};
        background: ${isSelected ? `${tintHex}22` : 'rgba(255,255,255,.04)'};
        color: #e8f1ff;
        font-size: 13px;
        text-align: left;
        cursor: pointer;
      `;
      card.innerHTML = `
        <div style="display:flex; align-items:center; gap:6px; margin-bottom:3px;">
          <span style="font-size:18px;">${opt.icon}</span>
          <span style="font-weight:600; color:${tintHex};">${opt.label}</span>
        </div>
        <div style="font-size:11px; color:#aac4d8; line-height:1.4;">${opt.shortDesc}</div>
      `;
      card.addEventListener('click', () => {
        // 冲突解除：A1/A2 不能同 key
        if (registryKey === 'skillA1' || registryKey === 'skillA2') {
          const other = registryKey === 'skillA1' ? 'skillA2' : 'skillA1';
          if (this.registry.get(other) === opt.key) {
            // 把另一槽换成第一个不冲突的候选
            const alt = options.find((o) => o.key !== opt.key);
            if (alt) this.registry.set(other, alt.key);
          }
        }
        this.registry.set(registryKey, opt.key);
        // 同步刷新菜单按钮文字
        this._refreshSkillBtn();
        onChange();
      });
      grid.appendChild(card);
    }
    wrap.appendChild(grid);
    container.appendChild(wrap);
  }


  /** 教学页内容：3 屏轮播（基础操作 / 怪物图鉴 / 进阶系统） */
  private _howToPages = [
    {
      title: '基础操作',
      body: `
        <ul style="margin:0; padding-left:20px; line-height:1.8;">
          <li><b style="color:#4cd2ff;">拖动移动</b>：除右下角技能区外，任意位置拖动操控坦克，自动开火</li>
          <li><b style="color:#ff5566;">大招（最上）</b>：屏幕右下角竖列<b>最上方</b>的圆按钮，击杀敌人蓄能</li>
          <li><b style="color:#ffd166;">技能 1 / 2</b>：右下角中间和最下的两个按钮，冷却复用</li>
          <li><b style="color:#c084ff;">菜单 → 技能</b>：可在 5 个技能里挑两个主动 + 一个大招</li>
          <li><b style="color:#ff8a8a;">生命</b>：撞到敌人 / 被弹幕命中扣血，0 = 死亡</li>
        </ul>
      `,
    },
    {
      title: '怪物图鉴',
      body: `
        <ul style="margin:0; padding-left:20px; line-height:1.7;">
          <li><b style="color:#cfd6e0;">普通小怪</b>：从顶部下落，会追玩家</li>
          <li><b style="color:#c084ff;">紫色精英</b>：血厚分多，金币掉得多</li>
          <li><b style="color:#ff8a3c;">橙色射手</b>：会朝你打子弹，先解决它</li>
          <li><b style="color:#ff5566;">红色自爆</b>：贴脸倒计时，圆形范围伤害，<u>抢先打死</u></li>
          <li><b style="color:#6ad6ff;">青色护盾兵</b>：正面减伤 80%，需要<b>穿透弹</b>破盾</li>
          <li><b style="color:#ffe066;">★ Boss</b>：每 5 波一只，血量 50% 进入狂暴</li>
        </ul>
      `,
    },
    {
      title: '进阶系统',
      body: `
        <ul style="margin:0; padding-left:20px; line-height:1.7;">
          <li><b style="color:#ffd166;">金宝箱</b>：每收 3 个开升级面板（选 1 张词条永久强化）</li>
          <li><b style="color:#ff6a3c;">🔥 火焰</b> / <b style="color:#6ad6ff;">❄ 冰冻</b> / <b style="color:#c0a8ff;">⚡ 闪电</b>：彩色独立箱子，撞到立刻得到 8 秒元素弹</li>
          <li><b style="color:#4cd2ff;">★ 稀有度</b>：白 → 蓝 → 紫 → 金，金色传说能定流派</li>
          <li><b style="color:#ffe066;">商店</b>：金币可重抽 / 祝福抽（必出稀有+）/ 回血</li>
          <li><b style="color:#ff8a8a;">天气</b>：每波随机 —— 狂风 / 磁暴 / 陨石 / 财源</li>
          <li><b style="color:#9ee37d;">连击</b>：1.5 秒内连杀，10 段以上伤害 +30%</li>
        </ul>
      `,
    },
  ];

  private _buildHowToDom(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; inset: 0;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.78);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      padding: 16px;
      box-sizing: border-box;
    `;
    const panel = document.createElement('div');
    panel.style.cssText = `
      width: min(86vw, 380px);
      max-height: calc(100vh - 32px);
      overflow-y: auto;
      padding: 20px 22px;
      border-radius: 18px;
      background: linear-gradient(180deg, #1d2a44 0%, #0e1424 100%);
      box-shadow: 0 18px 60px rgba(0,0,0,.65), 0 0 0 1px rgba(76,210,255,.28) inset, 0 0 34px rgba(76,210,255,.12);
      color: #e8f1ff;
    `;
    overlay.appendChild(panel);

    const renderPage = () => {
      const p = this._howToPages[this._howToPage];
      panel.innerHTML = `
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <div style="font-size:18px; font-weight:600; color:#4cd2ff;">${p.title}</div>
          <div style="font-size:12px; color:#5a6a80;">${this._howToPage + 1} / ${this._howToPages.length}</div>
        </div>
        <div style="font-size:13px; color:#cfd6e0;">${p.body}</div>
        <div data-howto-nav style="display:flex; gap:8px; margin-top:18px;"></div>
      `;
      const nav = panel.querySelector('[data-howto-nav]') as HTMLElement;
      // 上一页
      if (this._howToPage > 0) {
        const prev = document.createElement('button');
        prev.textContent = '上一页';
        prev.style.cssText = this._howToBtnCss('#aac4d8', false);
        prev.addEventListener('click', () => { this._howToPage -= 1; renderPage(); });
        nav.appendChild(prev);
      }
      // 主按钮：下一页 / 开始游戏
      const main = document.createElement('button');
      const isLast = this._howToPage === this._howToPages.length - 1;
      main.textContent = isLast ? '我知道了' : '下一页 →';
      main.style.cssText = this._howToBtnCss('#4cd2ff', true);
      main.addEventListener('click', () => {
        if (isLast) {
          this._destroyHowToPlay();
        } else {
          this._howToPage += 1;
          renderPage();
        }
      });
      nav.appendChild(main);
    };
    renderPage();

    return overlay;
  }

  private _howToBtnCss(color: string, primary: boolean): string {
    return `
      flex: 1; padding: 11px;
      border-radius: 8px;
      border: 1px solid ${color};
      background: ${primary ? `${color}22` : 'transparent'};
      color: ${color};
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    `;
  }

  private _resetHighScore(): void {
    resetHighScore();
    this._highScoreText.setText(this._highScoreLine());

    const hint = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 80, '已重置 ✓', {
        fontFamily: 'sans-serif',
        fontSize: '13px',
        color: '#9ee37d',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.tweens.add({
      targets: hint,
      alpha: 1,
      duration: 200,
      yoyo: true,
      hold: 600,
      onComplete: () => hint.destroy(),
    });
  }

  private _buildButton(
    x: number,
    y: number,
    text: string,
    accent: string,
    onClick: () => void,
    opts: { width?: number; height?: number; fontSize?: string } = {},
  ): MenuButton {
    const w = opts.width ?? 240;
    const h = opts.height ?? 56;
    const accentNum = Phaser.Display.Color.HexStringToColor(accent).color;
    const bg = this.add.rectangle(0, 0, w, h, 0x1a2236, 1).setStrokeStyle(2, accentNum, 0.7);
    const label = this.add
      .text(0, 0, text, {
        fontFamily: 'sans-serif',
        fontSize: opts.fontSize ?? '18px',
        fontStyle: 'bold',
        color: accent,
        align: 'center',
      })
      .setOrigin(0.5);

    const container = this.add.container(x, y, [bg, label]).setSize(w, h);
    container.setInteractive({ useHandCursor: true });
    container.on('pointerover', () => {
      bg.setFillStyle(0x223047, 1);
      bg.setStrokeStyle(2, accentNum, 1);
      this.tweens.add({ targets: container, scale: 1.03, duration: 90, ease: 'Sine.easeOut' });
    });
    container.on('pointerout', () => {
      bg.setFillStyle(0x1a2236, 1);
      bg.setStrokeStyle(2, accentNum, 0.7);
      this.tweens.add({ targets: container, scale: 1, duration: 100, ease: 'Sine.easeOut' });
    });
    container.on('pointerdown', () => {
      bg.setFillStyle(accentNum, 0.14);
      this.tweens.add({
        targets: container,
        scale: { from: 0.96, to: 1.03 },
        duration: 120,
        ease: 'Back.easeOut',
        onComplete: () => bg.setFillStyle(0x223047, 1),
      });
      onClick();
    });

    return { container, label };
  }

  /**
   * 右上角全屏按钮 —— 圆形 ⛶ 图标。点击切换全屏；Esc / 系统手势退出后能反映回来。
   * 必须在 pointerdown 回调里同步调用 toggleFullscreen，否则浏览器拒绝。
   */
  private _buildFullscreenButton(): void {
    const r = 18;
    const x = GAME_WIDTH - r - 12;
    const y = r + 12;

    const bg = this.add.circle(x, y, r, 0x000000, 0.4).setDepth(101);
    bg.setStrokeStyle(2, 0xffffff, 0.6);
    const icon = this.add
      .text(x, y, isFullscreen() ? '⤢' : '⛶', {
        fontFamily: 'sans-serif',
        fontSize: '18px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(102);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', () => {
      toggleFullscreen();
      // 切换有时是异步，过一帧再读取真实状态刷图标
      this.time.delayedCall(80, () => icon.setText(isFullscreen() ? '⤢' : '⛶'));
    });
    bg.on('pointerover', () => bg.setStrokeStyle(2, 0x4cd2ff, 1));
    bg.on('pointerout', () => bg.setStrokeStyle(2, 0xffffff, 0.6));

    // 系统层切回（Esc / 手势）也要刷图标
    const refresh = () => icon.setText(isFullscreen() ? '⤢' : '⛶');
    document.addEventListener('fullscreenchange', refresh);
    this.events.once('shutdown', () => {
      document.removeEventListener('fullscreenchange', refresh);
    });
  }
}
